import { useMemo } from "react";
import api from "../api";
import { enumLabel, type PlatformEnumOption } from "./enumLabel";

const ENUM_CACHE_SEC = 60 * 60 * 24;

/** One client-facing status of GET /runs/client-statuses/. */
export interface ClientRunStatusOption extends PlatformEnumOption {
  /** Staff may set it by hand - the override picker offers exactly these. */
  settable: boolean;
}

export interface TimezoneOption {
  value: string;
  label: string;
  abbreviation: string;
}

export const platformEnumsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getRunStatuses: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/runs/statuses/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getRunTriggerTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/runs/trigger-types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    // Client-facing status vocabulary (CAL-5773); reachable by client users.
    getClientRunStatuses: builder.query<ClientRunStatusOption[], void>({
      query: () => ({
        url: "/integration-platform/runs/client-statuses/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getIntegrationStepStages: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/steps/stages/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getCredentialTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/credentials/types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getConnectionTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/connections/types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getExecutionTargets: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/execution-targets/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getTimezones: builder.query<TimezoneOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/timezones/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getWebhookProviders: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/webhook-triggers/providers/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRunStatusesQuery,
  useGetRunTriggerTypesQuery,
  useGetClientRunStatusesQuery,
  useGetIntegrationStepStagesQuery,
  useGetCredentialTypesQuery,
  useGetConnectionTypesQuery,
  useGetExecutionTargetsQuery,
  useGetTimezonesQuery,
  useGetWebhookProvidersQuery,
} = platformEnumsApi;

export interface PlatformEnumLabelsOptions {
  /**
   * Skip the internal catalogs (run statuses, stages, trigger types): they
   * sit behind the internal read tier, so a client user - or an admin
   * previewing one in Client View - must not request them.
   */
  skipInternal?: boolean;
}

export function usePlatformEnumLabels({
  skipInternal = false,
}: PlatformEnumLabelsOptions = {}) {
  const { data: statuses } = useGetRunStatusesQuery(undefined, {
    skip: skipInternal,
  });
  const { data: stages } = useGetIntegrationStepStagesQuery(undefined, {
    skip: skipInternal,
  });
  const { data: triggers } = useGetRunTriggerTypesQuery(undefined, {
    skip: skipInternal,
  });
  const { data: clientStatuses } = useGetClientRunStatusesQuery();

  return useMemo(
    () => ({
      statusLabel: (value: string | null | undefined) =>
        enumLabel(statuses, value),
      stageLabel: (value: string | null | undefined) =>
        enumLabel(stages, value),
      triggerLabel: (value: string | null | undefined) =>
        enumLabel(triggers, value),
      clientStatusLabel: (value: string | null | undefined) =>
        enumLabel(clientStatuses, value),
    }),
    [statuses, stages, triggers, clientStatuses],
  );
}
